// Reception lock screen — RON-5469 R4. Prototype region: the `lock-overlay` built in JS.
//
// THE PIN IS CHECKED BY THE SERVER (§7b). The prototype's version opened for any four digits,
// which a security review would have taken on trust. The reason it cannot be done here:
//
//   "The browser at the desk still holds a valid Ronspot session while the screen is locked, so a
//    check done in JavaScript is a check a passer-by skips by reloading the page."
//
// So this component collects four digits and asks. It holds no secret, and it cannot be talked
// into opening by anything the browser knows.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const PIN_LENGTH = 4;

const formatWait = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const ReceptionLockScreen = ({ isLocked, siteName, error, lockedForSeconds, isBusy, onUnlock }) => {
  const { t } = useTranslation("common");
  const [pin, setPin] = useState("");
  const [remaining, setRemaining] = useState(0);
  const inputRef = useRef(null);

  // Focus the box the moment the screen locks, so the desk can simply type. Without it the first
  // four keystrokes go nowhere and the receptionist thinks the screen has frozen.
  useEffect(() => {
    if (!isLocked) return;

    setPin("");
    const timer = setTimeout(() => inputRef.current?.focus(), 50);

    return () => clearTimeout(timer);
  }, [isLocked]);

  // The countdown is driven from the server's `locked_for_seconds`, not from a local clock started
  // at the refusal — "show the wait ... rather than leaving the desk pressing a dead button".
  useEffect(() => {
    setRemaining(lockedForSeconds || 0);
  }, [lockedForSeconds]);

  useEffect(() => {
    if (remaining <= 0) return undefined;

    const timer = setInterval(() => setRemaining((current) => Math.max(0, current - 1)), 1000);

    return () => clearInterval(timer);
  }, [remaining > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLocked) return null;

  const isLockedOut = remaining > 0;
  const canSubmit = pin.length === PIN_LENGTH && !isBusy && !isLockedOut;

  const submit = () => {
    if (!canSubmit) return;

    onUnlock?.(pin);
    setPin("");
  };

  return (
    // No close button and no backdrop dismiss: a lock you can click past is not a lock. The only
    // way out is the PIN, or closing the tab.
    <div className="reception-lock" role="dialog" aria-modal="true" aria-label={t("react_reception_locked")}>
      <div className="reception-lock__card">
        <div className="reception-lock__icon" aria-hidden="true">🔒</div>
        <h2 className="reception-lock__title">{t("react_reception_locked")}</h2>
        <p className="reception-lock__who">{siteName}</p>

        {isLockedOut ? (
          <>
            <p className="reception-lock__prompt">{t("react_reception_lock_too_many")}</p>
            <p className="reception-lock__countdown" role="status">
              {formatWait(remaining)}
            </p>
          </>
        ) : (
          <>
            <p className="reception-lock__prompt">{t("react_reception_lock_prompt")}</p>

            <input
              ref={inputRef}
              className="reception-lock__pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={PIN_LENGTH}
              value={pin}
              // Digits only, and never longer than the PIN. Typing a letter should do nothing
              // rather than silently fail four keystrokes later.
              onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH))}
              onKeyDown={(event) => event.key === "Enter" && submit()}
              aria-label={t("react_reception_lock_prompt")}
              placeholder="••••"
            />

            {/* The server's own words. It says "That PIN was not recognised" whether the PIN was
                wrong or the site has none set — it never volunteers that there is nothing to
                guess — so this renders the message rather than interpreting it. */}
            {error && (
              <p className="reception-lock__error" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              className="reception-btn reception-lock__resume"
              disabled={!canSubmit}
              onClick={submit}
            >
              {t("react_reception_resume")}
            </button>
          </>
        )}

        <p className="reception-lock__note">{t("react_reception_lock_note")}</p>
      </div>
    </div>
  );
};

export default ReceptionLockScreen;
